// components
import { MenuItem } from '@mui/material';
import GridPageAbstract from '../abstract/GridPageAbstract';
import Iconify from '../../components/iconify';
import { mergeHeadWithStorage } from '../../utils/gridHeadStorage';
import { formatApiErrors } from '../../utils/formatError';
import { scenarioEditorPath } from '../scraper/scenarioPaths';
import i18n from '../../i18n';

/**
 * Scraper scenarios: named scripts x-scraper runs in a headless browser, which
 * xscraper gateway settings reference instead of carrying inline code.
 *
 * Code is not something the generic editor dialog can hold — it wants a real
 * editor, and a run against the browser to see what it does — so adding and
 * editing open ScraperScenarioEditorPage instead. That is done in dialogOpen,
 * the one place the Add button and every edit path go through. The grid's own
 * "Edit" item is dropped (disableGlobalEditor) for "Open editor", which is also
 * there for a role that may read scenarios but not change them: the editor is
 * where the code can be read at all.
 *
 * Delete is the standard flow. The database refuses it, like a rename or an
 * archive, while gateway settings still reference the scenario
 * (TRIGGER_SCRAPER_SCENARIO_IN_USE).
 */
export default class ScraperScenariosPage extends GridPageAbstract {
    constructor(props) {
        super(props);
        this.Name = 'publicScraperScenarios';
        this.Server = this.props.context.getState().server.publicScraperScenarios;
        this.MenuDisplayType = 'menu';
        this.searchFields = ['name', 'description'];
        this.disableGlobalEditor = true;

        const defaultHead = [
            { id: 'name', required: true, label: 'name', alignRight: false, cell: 'Text', displayGrid: true },
            { id: 'description', label: 'description', alignRight: false, cell: 'Text', displayGrid: true },
            {
                id: 'modifiedBy',
                label: 'modifiedBy',
                alignRight: false,
                cell: 'Select',
                object: 'modifiedBy',
                show: 'firstName+lastName',
                displayGrid: true,
                disableEditor: true,
                customSort: 'modifiedBy.firstName'
            },
            {
                id: 'dateModified',
                label: 'dateModified',
                alignRight: false,
                cell: 'DateTime',
                displayGrid: true,
                disableEditor: true
            },
            { id: 'isArchived', label: 'isArchived', alignRight: false, cell: 'Boolean', displayGrid: true }
        ];
        this.Head = mergeHeadWithStorage(this.Name, defaultHead);

        this.state = {
            ...this.state,
            head: this.Head
        };
    }

    /** Add (no row) and edit (a row) both open the editor page. */
    dialogOpen(data) {
        this.handleCloseMenu();
        this.props.navigate(scenarioEditorPath(data?.id));
    }

    openEditor = () => {
        const row = this.getMenuClickItem();
        this.dialogOpen(row);
    };

    /**
     * The base shows only result.error — "API_ERROR" — which hides the one
     * refusal this grid expects: that gateway settings still use the scenario.
     */
    handleDeleteItem = async () => {
        const result = await this.Server.delete(
            this.state.menuClickItemId ? [this.state.menuClickItemId] : this.state.selected
        );
        this.handleCloseConfirmDialog();
        if (result.error !== 'OK') {
            this.props.context.update({
                alertError: { message: formatApiErrors(result).join('\n') }
            });
        } else {
            await this.retrieveData();
        }
    };

    renderCustomMenuItems(id) {
        // An array, not a fragment: hasMenuItems counts custom items with
        // Array.isArray(...) && length > 0, so a bare element does not register.
        return [
            <MenuItem key={`${id}-open-editor`} onClick={this.openEditor}>
                <Iconify icon="eva:code-outline" sx={{ mr: 2 }} />
                {i18n.t('scraperScenarios.openEditor')}
            </MenuItem>
        ];
    }
}
